import React from "react";
import { AssociatedPerson } from "../../../../types/cdd-records.types";
import { formatDateShort } from "../../../../utils/dateFormatters";

const roleColors: Record<string, { bg: string; border: string; text: string }> =
  {
    trustee: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-700",
    },
    beneficiary: {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-700",
    },
    settlor: {
      bg: "bg-purple-50",
      border: "border-purple-200",
      text: "text-purple-700",
    },
    appointer: {
      bg: "bg-orange-50",
      border: "border-orange-200",
      text: "text-orange-700",
    },
    nominee: {
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-700",
    },
  };

export const TrustPersonCard: React.FC<{
  person: AssociatedPerson;
  roleType: string;
  onClick?: (person: AssociatedPerson) => void;
}> = ({ person, roleType, onClick }) => {
  const colors = roleColors[roleType] || roleColors.nominee;

  return (
    <div
      onClick={() => onClick?.(person)}
      className={`p-4 rounded-lg border ${colors.border} ${colors.bg} hover:shadow-md cursor-pointer transition-all`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full ${colors.bg} border-2 ${colors.border} flex items-center justify-center`}
          >
            <span className={`font-semibold ${colors.text}`}>
              {person.firstName[0]}
              {person.lastName[0]}
            </span>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900">
              {person.firstName} {person.lastName}
            </h4>
            {person.occupation && (
              <p className="text-xs text-gray-500">{person.occupation}</p>
            )}
          </div>
        </div>
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded ${
            person.verificationStatus === "verified"
              ? "bg-green-100 text-green-800"
              : person.verificationStatus === "pending"
                ? "bg-yellow-100 text-yellow-800"
                : person.verificationStatus === "failed"
                  ? "bg-red-100 text-red-800"
                  : "bg-gray-100 text-gray-800"
          }`}
        >
          {person.verificationStatus.replace("-", " ").replace(/\b\w/g, c => c.toUpperCase())}
        </span>
      </div>

      {/* Additional Info */}
      <div className="mt-3 space-y-1 text-xs text-gray-500">
        {person.ownershipPercentage !== undefined && (
          <div className="flex items-center gap-2">
            <span>Interest:</span>
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div
                className={`${colors.bg.replace("50", "500")} h-1.5 rounded-full`}
                style={{
                  width: `${Math.min(person.ownershipPercentage, 100)}%`,
                }}
              />
            </div>
            <span className="font-medium">{person.ownershipPercentage}%</span>
          </div>
        )}
        {person.appointmentDate && (
          <div>Appointed: {formatDateShort(person.appointmentDate)}</div>
        )}
      </div>

      {/* Risk Indicators */}
      {(person.isPEP || person.isSanctioned) && (
        <div className="mt-2 flex gap-2">
          {person.isPEP && (
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800">
              PEP
            </span>
          )}
          {person.isSanctioned && (
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">
              Sanctioned
            </span>
          )}
        </div>
      )}
    </div>
  );
};
